import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaHeart } from "react-icons/fa";
import { CiHeart } from "react-icons/ci";
import { BsBasket } from "react-icons/bs";
import { useWishlist } from "../context/WishlistContext";
import { CartContext } from "../context/CartContext";
import {
  isProductInStock,
  sortProductsByAvailability,
} from "../utils/productAvailability";
import { getWishlistProductId } from "../utils/wishlistProducts";
import { buildProductDetailPath } from "../utils/productLinks";
import { getProductImage } from "../utils/productImages";
import { getProductDisplayName } from "../utils/productDisplayName";
import {
  getProductDiscountPercent,
  getProductEffectivePrice,
  getProductRegularPrice,
} from "../utils/productSorting";
import ProductRatingSummary from "./ProductRatingSummary";
import DiscountBadge from "./DiscountBadge";

const formatPrice = (value) =>
  new Intl.NumberFormat("uk-UA", {
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const WishlistProductGrid = ({
  cardStyles,
  sectionClassName,
  emptyClassName,
  isLoading = false,
}) => {
  const { wishlist, removeFromWishlist, isInWishlist, addToWishlist } =
    useWishlist();
  const { addToCart } = useContext(CartContext);
  const [successMessage, setSuccessMessage] = useState("");
  const sortedWishlist = useMemo(
    () => sortProductsByAvailability(wishlist),
    [wishlist],
  );

  if (isLoading) {
    return <div className={emptyClassName}>Завантаження...</div>;
  }

  if (!wishlist || wishlist.length === 0) {
    return (
      <div className={emptyClassName}>
        <p>
          Список обраних товарів порожній! Додавайте товари, натиснувши{" "}
          <span style={{ color: "#e53935" }}>
            <FaHeart size={22} />
          </span>{" "}
          у каталозі!
        </p>
      </div>
    );
  }

  return (
    <section className={sectionClassName}>
      {successMessage && (
        <div className={cardStyles.successMessage}>{successMessage}</div>
      )}
      <div className={cardStyles.productsContainer}>
        {sortedWishlist.map((product) => {
          const productId = getWishlistProductId(product);
          const discountPercent = Math.round(getProductDiscountPercent(product));
          const regularPrice = getProductRegularPrice(product);
          const currentPrice = getProductEffectivePrice(product);
          const isOutOfStock = !isProductInStock(product);

          return (
            <div key={productId} className={cardStyles.productCard}>
              <button
                type="button"
                className={`${cardStyles.wishlistHeartButton} ${
                  isInWishlist(productId) && cardStyles.activeWishlistButton
                    ? cardStyles.activeWishlistButton
                    : ""
                }`}
                onClick={(event) => {
                  event.preventDefault();
                  isInWishlist(productId)
                    ? removeFromWishlist(productId)
                    : addToWishlist(product);
                }}
                aria-label={
                  isInWishlist(productId)
                    ? "Видалити з обраного"
                    : "Додати в обране"
                }
              >
                {isInWishlist(productId) ? (
                  <FaHeart color="#e53935" />
                ) : (
                  <CiHeart color="#e53935" />
                )}
              </button>

              <Link
                to={buildProductDetailPath(product)}
                className={cardStyles.productLink}
              >
                <div className={cardStyles.productCardImageWrap}>
                  <img
                    src={getProductImage(product, "thumbnail")}
                    alt={product.name}
                    className={`${cardStyles.productImage} ${
                      isOutOfStock && cardStyles.mutedProductImage
                        ? cardStyles.mutedProductImage
                        : ""
                    }`}
                    loading="lazy"
                    decoding="async"
                  />
                  {discountPercent > 0 && !isOutOfStock && (
                    <DiscountBadge percent={discountPercent} />
                  )}
                </div>
                <h4 className={cardStyles.productTitle}>
                  {getProductDisplayName(product)}
                </h4>
              </Link>

              <ProductRatingSummary product={product} />

              <div className={cardStyles.cardBottomCentered}>
                {isOutOfStock ? (
                  <div className={cardStyles.priceWrapperCentered}>
                    {regularPrice > 0 && (
                      <span className={cardStyles.disabledPrice}>
                        {formatPrice(regularPrice)} ₴
                      </span>
                    )}
                    <span className={cardStyles.outOfStockLabel}>
                      Немає в наявності
                    </span>
                  </div>
                ) : (
                  <>
                    {discountPercent > 0 ? (
                      <div className={cardStyles.priceWrapperCentered}>
                        <span className={cardStyles.oldPrice}>
                          {formatPrice(regularPrice)} ₴
                        </span>
                        <span className={cardStyles.discountPrice}>
                          {formatPrice(currentPrice)} ₴
                        </span>
                      </div>
                    ) : (
                      <p className={cardStyles.productPrice}>
                        {formatPrice(regularPrice)} ₴
                      </p>
                    )}

                    <button
                      type="button"
                      className={cardStyles.addToCartIconButtonBottom}
                      onClick={(event) => {
                        event.preventDefault();
                        addToCart(product, 1);
                        setSuccessMessage("Товар додано до кошика!");
                        setTimeout(() => setSuccessMessage(""), 3000);

                        const button = event.currentTarget;
                        setTimeout(() => {
                          button.blur();
                        }, 500);
                      }}
                      onTouchEnd={(event) => {
                        event.currentTarget.blur();
                      }}
                      title="Додати в кошик"
                    >
                      <BsBasket />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default WishlistProductGrid;
